import React, { useState, useEffect, useCallback } from "react";
import { useClub } from "../contexts/ClubContext";
import { apiService } from "../services/api";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input } from "../components/ui/Input";
import { ClubCard } from "../components/ui/ClubCard";
import { generateAccessCode } from "../utils/helpers";
import toast from "react-hot-toast";

const SuperAdminDashboard = () => {
  const { clubs, createClub, loadClubs, loading } = useClub();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [newClub, setNewClub] = useState({
    name: "",
    location: "",
    adminEmail: "",
    adminName: "",
    subscriptionPlanId: "",
    billingStartDate: "",
    billingEndDate: "",
    autoRenew: true,
  });
  const [editingClub, setEditingClub] = useState(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);

  const loadSubscriptionPlans = useCallback(async () => {
    try {
      const plans = await apiService.getSubscriptionPlans();
      setSubscriptionPlans(plans);
    } catch (error) {
      console.error("Failed to load subscription plans:", error);
    }
  }, []);

  useEffect(() => {
    loadClubs();
    loadSubscriptionPlans();
  }, [loadClubs, loadSubscriptionPlans]);

  const handleCreateClub = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const clubData = {
        name: newClub.name,
        location: newClub.location,
        adminEmail: newClub.adminEmail,
        adminName: newClub.adminName,
        accessCode: generateAccessCode(),
        subscriptionPlanId: newClub.subscriptionPlanId,
        billingStartDate: newClub.billingStartDate,
        billingEndDate: newClub.billingEndDate,
        autoRenew: newClub.autoRenew,
      };

      const createdClub = await createClub(clubData);

      setShowCreateModal(false);
      setNewClub({
        name: "",
        location: "",
        adminEmail: "",
        adminName: "",
        subscriptionPlanId: "",
        billingStartDate: "",
        billingEndDate: "",
        autoRenew: true,
      });
      toast.success(`Club ${createdClub.name} created successfully!`);
      loadClubs(); // Reload to get updated data
    } catch (error) {
      toast.error("Failed to create club");
    }

    setCreating(false);
  };

  const handleEditClub = (club) => {
    console.log("Editing club:", club);
    setEditingClub({
      ...club,
      subscriptionPlanId: club.subscription_plan_id || "",
      billingStartDate: club.billing_start_date || "",
      billingEndDate: club.billing_end_date || "",
      autoRenew: club.auto_renew !== undefined ? club.auto_renew : true,
    });
    setShowEditModal(true);
  };

  const handleUpdateClub = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const updates = {
        name: editingClub.name,
        location: editingClub.location,
      };

      await apiService.updateClub(editingClub.id, updates);

      // If subscription plan changed, update it
      if (editingClub.subscriptionPlanId !== editingClub.subscription_plan_id) {
        try {
          await apiService.assignSubscriptionToClub(editingClub.id, {
            subscriptionPlanId: editingClub.subscriptionPlanId || null,
            startDate: editingClub.billingStartDate,
            endDate: editingClub.billingEndDate,
            autoRenew: editingClub.autoRenew,
          });
        } catch (subscriptionError) {
          toast.error("Club updated but failed to update subscription plan");
        }
      }

      setShowEditModal(false);
      setEditingClub(null);
      toast.success("Club updated successfully!");
      loadClubs(); // Reload to get updated data
    } catch (error) {
      toast.error("Failed to update club");
    }

    setUpdating(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // If subscription plan is being changed, auto-set billing dates
    if (name === "subscriptionPlanId" && value) {
      const today = new Date();
      const oneYearFromToday = new Date(today);
      oneYearFromToday.setFullYear(today.getFullYear() + 1);

      setNewClub({
        ...newClub,
        [name]: value,
        billingStartDate: today.toISOString().split("T")[0], // Format as YYYY-MM-DD
        billingEndDate: oneYearFromToday.toISOString().split("T")[0], // Format as YYYY-MM-DD
      });
    } else {
      setNewClub({
        ...newClub,
        [name]: value,
      });
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;

    // If subscription plan is being changed and a plan is selected, auto-set billing dates
    if (
      name === "subscriptionPlanId" &&
      value &&
      value !== editingClub.subscription_plan_id
    ) {
      const today = new Date();
      const oneYearFromToday = new Date(today);
      oneYearFromToday.setFullYear(today.getFullYear() + 1);

      setEditingClub({
        ...editingClub,
        [name]: value,
        billingStartDate: today.toISOString().split("T")[0], // Format as YYYY-MM-DD
        billingEndDate: oneYearFromToday.toISOString().split("T")[0], // Format as YYYY-MM-DD
      });
    } else {
      setEditingClub({
        ...editingClub,
        [name]: value,
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Super Admin Dashboard
          </h1>
          <p className="text-gray-600">Manage clubs and organizations</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          Create New Club
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <div className="p-6">
            <div className="flex items-center">
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Clubs</p>
                <p className="text-2xl font-bold text-gray-900">
                  {clubs.length}
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <div className="flex items-center">
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Active Clubs
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {clubs.filter((club) => club.status === "active").length}
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <div className="flex items-center">
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Active Evaluations
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {clubs.reduce(
                    (total, club) => total + (club.active_evaluations || 0),
                    0,
                  )}
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <div className="flex items-center">
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Completed Evaluations
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {clubs.reduce(
                    (total, club) => total + (club.completed_evaluations || 0),
                    0,
                  )}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Clubs Grid */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            All Clubs ({clubs.length})
          </h2>

          {clubs.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gray-400 text-2xl"></span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No clubs yet
              </h3>
              <p className="text-gray-600 mb-4">
                Get started by creating your first club.
              </p>
              <Button onClick={() => setShowCreateModal(true)}>
                Create First Club
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {clubs.map((club) => (
                <ClubCard key={club.id} club={club} onEdit={handleEditClub} />
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Create Club Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Club"
      >
        <form onSubmit={handleCreateClub} className="space-y-4">
          <Input
            name="name"
            placeholder="Club Name (e.g., Springfield Lacrosse Club)"
            value={newClub.name}
            onChange={handleInputChange}
            required
          />

          <Input
            name="location"
            placeholder="Location (e.g., Springfield, MA)"
            value={newClub.location}
            onChange={handleInputChange}
            required
          />

          <Input
            name="adminName"
            placeholder="Admin Name"
            value={newClub.adminName}
            onChange={handleInputChange}
            required
          />

          <Input
            name="adminEmail"
            type="email"
            placeholder="Admin Email"
            value={newClub.adminEmail}
            onChange={handleInputChange}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subscription Plan
            </label>
            <select
              name="subscriptionPlanId"
              value={newClub.subscriptionPlanId}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            >
              <option value="">Select a plan (optional)</option>
              {subscriptionPlans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} - ${plan.price_per_player_per_year}/player per
                  evaluation
                </option>
              ))}
            </select>
          </div>

          {newClub.subscriptionPlanId && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Billing Start Date
                </label>
                <Input
                  name="billingStartDate"
                  type="date"
                  value={newClub.billingStartDate}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Billing End Date (Optional)
                </label>
                <Input
                  name="billingEndDate"
                  type="date"
                  value={newClub.billingEndDate}
                  onChange={handleInputChange}
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="autoRenew"
                  name="autoRenew"
                  checked={newClub.autoRenew}
                  onChange={(e) =>
                    setNewClub({
                      ...newClub,
                      autoRenew: e.target.checked,
                    })
                  }
                  className="h-4 w-4 text-slate-600 focus:ring-slate-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="autoRenew"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Auto-renew subscription
                </label>
              </div>
            </>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-yellow-900 mb-1">
              What happens next?
            </h4>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• A unique access code will be generated</li>
              <li>• Admin will receive login credentials via email</li>
              <li>• Club will be ready for player imports and evaluations</li>
            </ul>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={creating || !newClub.name || !newClub.adminEmail}
            >
              {creating ? "Creating..." : "Create Club"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Club Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Club"
      >
        {editingClub && (
          <form onSubmit={handleUpdateClub} className="space-y-4">
            <Input
              name="name"
              placeholder="Club Name"
              value={editingClub.name}
              onChange={handleEditInputChange}
              required
            />

            <Input
              name="location"
              placeholder="Location"
              value={editingClub.location}
              onChange={handleEditInputChange}
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subscription Plan
              </label>
              <select
                name="subscriptionPlanId"
                value={editingClub.subscriptionPlanId}
                onChange={handleEditInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              >
                <option value="">No subscription plan</option>
                {subscriptionPlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} - ${plan.price_per_player_per_year}/player per
                    evaluation
                  </option>
                ))}
              </select>
            </div>

            {editingClub.subscriptionPlanId && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Billing Start Date
                  </label>
                  <Input
                    name="billingStartDate"
                    type="date"
                    value={editingClub.billingStartDate}
                    onChange={handleEditInputChange}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Billing End Date (Optional)
                  </label>
                  <Input
                    name="billingEndDate"
                    type="date"
                    value={editingClub.billingEndDate}
                    onChange={handleEditInputChange}
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="editAutoRenew"
                    name="autoRenew"
                    checked={editingClub.autoRenew}
                    onChange={(e) =>
                      setEditingClub({
                        ...editingClub,
                        autoRenew: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-slate-600 focus:ring-slate-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="editAutoRenew"
                    className="ml-2 block text-sm text-gray-900"
                  >
                    Auto-renew subscription
                  </label>
                </div>
              </>
            )}

            <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
              <h4 className="text-sm font-medium text-slate-900 mb-1">Note</h4>
              <p className="text-sm text-slate-800">
                Changes to the subscription plan will take effect immediately.
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updating || !editingClub.name}>
                {updating ? "Updating..." : "Update Club"}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default SuperAdminDashboard;
